import { useWebsiteThemeSafe } from '@bridebook/web/components/wedding-website/website-design/website-design.context';
import { sortEventsByDate } from '@bridebook/web/lib/events/sort-events';
import { useSelector } from '@bridebook/web/lib/utils';
import { useWeddingEvents } from '@bridebook/web/lib/wedding-website/hooks/query';
import { isVenueSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import {
  getWebsiteDraftWebsite,
  getWebsitePartners,
  getWebsiteWeddingDate,
} from '@bridebook/web/lib/weddings/selectors';
import { createContext, useContext, useMemo } from 'react';
import { PhotosBySupplier, WeddingWebsiteDataContext } from './data-context';
import {
  toValidFontPair,
  toValidPalette,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/utils';
import { useBBSuppliers } from '@bridebook/web/lib/supplier/hooks/query/use-bb-suppliers';
import { useSupplierPhotos } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import { isCustomEvent } from '@bridebook/web/lib/events/types';
import { compact, uniq } from 'lodash';
import { useEditorScrollSync } from '../../../main/desktop/editor-sync-context';
import { DesktopPreviewDevice } from '@bridebook/web/lib/wedding-website/desktop/types';

const IsEditModeContext = createContext<boolean>(false);
export const useIsEditMode = () => useContext(IsEditModeContext) ?? false;

const IsSidebarEditorContext = createContext<boolean>(false);
export const useIsSidebarEditor = () => useContext(IsSidebarEditorContext) ?? false;

const ActivePreviewDeviceContext = createContext<DesktopPreviewDevice | undefined>(undefined);
export const useActivePreviewDevice = () => useContext(ActivePreviewDeviceContext) ?? undefined;

type Props = {
  editMode: boolean;
  isSidebarEditor: boolean;
  children: React.ReactNode;
  activePreviewDevice: DesktopPreviewDevice;
};

export const DataProviderPanel = ({
  editMode,
  isSidebarEditor,
  activePreviewDevice,
  children,
}: Props) => {
  const { data: events } = useWeddingEvents(sortEventsByDate);
  useEditorScrollSync();

  // wedding
  const partners = useSelector(getWebsitePartners);
  const weddingDate = useSelector(getWebsiteWeddingDate);

  //website
  const website = useSelector(getWebsiteDraftWebsite);

  // config
  const { font: localFont, palette: localPalette } = useWebsiteThemeSafe();

  // suppliers
  const venueSection = website.sections.find(isVenueSection);
  const mainSupplierId = venueSection ? venueSection.data.supplier : undefined;
  const useVenuePhotos = venueSection?.data.useVenuePhotos ?? false;
  const eventsSuppliersIds = events.filter(isCustomEvent).map((event) => event.supplier);
  const allSuppliersIds = uniq(compact([mainSupplierId, ...eventsSuppliersIds]));
  const { data: allSuppliers } = useBBSuppliers({
    supplierIds: allSuppliersIds,
  });

  const { data: venuePhotosData } = useSupplierPhotos(
    useVenuePhotos && mainSupplierId ? { supplierId: mainSupplierId } : undefined,
  );

  const photosBySupplier: PhotosBySupplier = useMemo(() => {
    if (!mainSupplierId || !venuePhotosData) return [];
    return [{ supplierId: mainSupplierId, photos: venuePhotosData.slice(0, 5) }];
  }, [mainSupplierId, venuePhotosData]);

  return (
    <IsEditModeContext.Provider value={editMode}>
      <IsSidebarEditorContext.Provider value={isSidebarEditor}>
        <ActivePreviewDeviceContext.Provider value={activePreviewDevice}>
          <WeddingWebsiteDataContext.Provider
            value={{
              wedding: {
                partners,
                date: weddingDate,
              },
              website: {
                ...website,
                config: {
                  ...website.config,
                  theme: {
                    palette: toValidPalette(localPalette),
                    font: toValidFontPair(localFont),
                  },
                },
              },
              events,
              suppliers: allSuppliers ?? [],
              photosBySupplier,
            }}>
            {children}
          </WeddingWebsiteDataContext.Provider>
        </ActivePreviewDeviceContext.Provider>
      </IsSidebarEditorContext.Provider>
    </IsEditModeContext.Provider>
  );
};
